if (!customElements.get('td-shade-swatches')) {
  class TdShadeSwatches extends HTMLElement {
    static SELECTORS = {
      topSwatch: '[data-td-top-swatch]',
      // NOTE (td-js-vanilla-rules selector exception): `.td-product-main` markup is
      // owned by the product-main sections (td-product-main-variation-swiper, etc.).
      // We look the picker up lazily rather than caching it so the reference survives
      // theme-editor `shopify:section:load` re-renders of that section.
      picker: '.td-product-main variant-radios, .td-product-main variant-selects',
    };

    static EVENTS = {
      primaryShadeChange: 'td:primary-shade-change',
    };

    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handlePickerChange = this.handlePickerChange.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handlePrimaryShadeChange = this.handlePrimaryShadeChange.bind(this);
      this.boundPicker = null;
    }

    connectedCallback() {
      this.colorOptionName = this.dataset.tdColorOptionName || '';

      this.addEventListener('click', this.handleClick);
      // Listen on the picker element itself rather than `document` so unrelated
      // form controls on the page don't trigger a sync. Re-bind on
      // `shopify:section:load` because the theme editor can re-render the
      // product-main section (and its picker) out from under us.
      this.bindPickerChange();
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      document.addEventListener(TdShadeSwatches.EVENTS.primaryShadeChange, this.handlePrimaryShadeChange);

      this.hideBrokenSwatchImages();
      this.syncFromPicker();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      if (this.boundPicker) {
        this.boundPicker.removeEventListener('change', this.handlePickerChange);
        this.boundPicker = null;
      }
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      document.removeEventListener(TdShadeSwatches.EVENTS.primaryShadeChange, this.handlePrimaryShadeChange);
    }

    resolvePicker() {
      return document.querySelector(TdShadeSwatches.SELECTORS.picker);
    }

    bindPickerChange() {
      const picker = this.resolvePicker();
      if (picker === this.boundPicker) return;
      if (this.boundPicker) this.boundPicker.removeEventListener('change', this.handlePickerChange);
      this.boundPicker = picker;
      if (picker) picker.addEventListener('change', this.handlePickerChange);
    }

    handleSectionLoad() {
      this.bindPickerChange();
      this.syncFromPicker();
    }

    handleClick(event) {
      const topSwatch = event.target.closest(TdShadeSwatches.SELECTORS.topSwatch);
      if (!topSwatch) return;

      const shadeValue = topSwatch.dataset.tdShadeValue;
      if (!shadeValue) return;

      this.selectShade(shadeValue);
    }

    handlePickerChange(event) {
      if (!this.colorOptionName) return;

      const target = event.target;
      if (!target) return;

      let optionName = null;
      if (target.matches('input[type="radio"]')) {
        optionName = target.name;
      } else if (target.matches('select')) {
        const match = (target.name || '').match(/^options\[(.+)\]$/);
        optionName = match ? match[1] : null;
      }

      if (optionName !== this.colorOptionName) return;

      this.updateTopSwatches(target.value);
    }

    handlePrimaryShadeChange(event) {
      const shadeValue = event.detail?.shadeValue;
      const inputName = event.detail?.inputName;

      if (!shadeValue) return;
      if (inputName && this.colorOptionName && inputName !== this.colorOptionName) return;

      this.selectShade(shadeValue);
    }

    selectShade(shadeValue) {
      if (!this.colorOptionName) return;

      const picker = this.resolvePicker();
      if (!picker) return;

      const radio = picker.querySelector(
        `input[type="radio"][name="${CSS.escape(this.colorOptionName)}"][value="${CSS.escape(shadeValue)}"]`
      );

      if (radio) {
        if (radio.checked) {
          this.updateTopSwatches(radio.value);
          return;
        }
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      // variant-selects fallback
      const select = picker.querySelector(`select[name="options[${CSS.escape(this.colorOptionName)}]"]`);
      if (!select) return;
      if (select.value === shadeValue) {
        this.updateTopSwatches(select.value);
        return;
      }
      select.value = shadeValue;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncFromPicker() {
      if (!this.colorOptionName) return;

      const picker = this.resolvePicker();
      if (!picker) return;

      const checkedRadio = picker.querySelector(
        `input[type="radio"][name="${CSS.escape(this.colorOptionName)}"]:checked`
      );
      if (checkedRadio) {
        this.updateTopSwatches(checkedRadio.value);
        return;
      }

      const select = picker.querySelector(`select[name="options[${CSS.escape(this.colorOptionName)}]"]`);
      if (select) {
        this.updateTopSwatches(select.value);
      }
    }

    hideBrokenSwatchImages() {
      this.querySelectorAll('.td-shade-finder__swatch-img').forEach((img) => {
        const hide = () => {
          img.style.display = 'none';
        };
        if (img.complete && img.naturalWidth === 0) {
          hide();
        } else {
          img.addEventListener('error', hide, { once: true });
        }
      });
    }

    updateTopSwatches(activeValue) {
      this.querySelectorAll(TdShadeSwatches.SELECTORS.topSwatch).forEach((swatch) => {
        const isActive = swatch.dataset.tdShadeValue === activeValue;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
  }

  customElements.define('td-shade-swatches', TdShadeSwatches);
}
